import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import io from "socket.io-client";
import { 
    Container, 
    Grid, 
    Box, 
    TextField, 
    List, 
    ListItem, 
    ListItemText, 
    Button, 
    Typography, 
    Stack, 
    Avatar, 
    Paper,
    Chip,
    IconButton,
    InputAdornment,
    Badge,
    Divider,
    Card,
    CardContent,
    Fade,
    Skeleton
} from "@mui/material";
import { 
    Search as SearchIcon, 
    Send as SendIcon, 
    ArrowBack as ArrowBackIcon,
    MoreVert as MoreVertIcon
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { capitalizeFirstLetter } from "../../utils/helpers";
import { Loader } from "../../components/CustomLoader";
import { IMAGE_BASE } from "../../utils/constants";
import { useMediaQuery, useTheme } from "@mui/material";

export const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_SERVER_URL

const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

export default function SupportChat() {
    const [socket, setSocket] = useState(null);
    const [inbox, setInbox] = useState([]);
    const [inboxPage, setInboxPage] = useState(1);
    const inboxLimit = 100;
    const [inboxTotalPages, setInboxTotalPages] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const selectedUserRef = useRef(selectedUser);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [messageInput, setMessageInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [hasMoreInbox, setHasMoreInbox] = useState(true);
    const chatBoxRef = useRef(null);
    const inboxScrollRef = useRef(null);
    const token = useSelector((state) => state.auth.token);
    const admin = useSelector((state) => state.auth?.user);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Sync ref to selectedUser
    useEffect(() => {
        selectedUserRef.current = selectedUser;
    }, [selectedUser]);

    // Scroll to bottom on new message
    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTo({
                top: chatBoxRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [chatMessages]);

    const fetchInbox = useCallback((page = 1, append = false) => {
        if (!socket) return;

        socket.emit(
            "getsupportInbox",
            { searchTerm, page, limit: inboxLimit },
            (response) => {
                const data = response?.data || [];
                const totalPages = response?.totalPages || 1;

                const updatedInbox = data.map((conv) => ({
                    ...conv,
                    userInfo: conv.userDetails || conv.otherUserInfo || {},
                    lastMessage: conv.message,
                    timeAgo: formatTimeAgo(conv.createdAt || new Date()),
                }));

                if (append) {
                    setInbox(prev => [...prev, ...updatedInbox]);
                } else {
                    setInbox(updatedInbox);
                }
                
                setInboxTotalPages(totalPages);
                setHasMoreInbox(page < totalPages);
            }
        );
    }, [socket, inboxLimit, searchTerm]);

    const fetchChatMessages = useCallback(
        (receiverId) => {
            if (!socket || !receiverId) return;
            setChatLoading(true);

            socket.emit(
                "getsupportChatHistory",
                { receiverId, page: 1, limit: 100 },
                (response) => {
                    const messages = response?.data || [];
                    const userInfo = response?.otherUserInfo || {};
                    setChatMessages(
                        messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                    );
                    setSelectedUser((prev) => ({
                        ...prev,
                        userId: userInfo._id || receiverId,
                        username: userInfo.username || prev.username,
                        firstname: userInfo.firstname || prev.firstname,
                        lastname: userInfo.lastname || prev.lastname,
                        image: userInfo.image || prev.image,
                    }));
                    setChatLoading(false);
                }
            );
        },
        [socket]
    );

    useEffect(() => {
        if (!token) return;

        const newSocket = io(SOCKET_SERVER_URL, {
            auth: { token: token },
        });

        newSocket.on("connect", () => {
        });
        newSocket.on("connect_error", (err) => {
            console.error("❌ Socket connection error:", err.message);
        });
        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [token]);

    const handleNewInboxMessage = (message) => {
        const senderId = message.sender?.id;
        const receiverId = message.receiver?.id;
        const isAdminSender = ["admin", "SuperAdmin", "Mgt", "Junior"].includes(message.sender?.role);
        const chatUserId = isAdminSender ? receiverId : senderId;
        const userInfo = isAdminSender ? message.receiver : message.sender;

        setInbox((prevInbox) => {
            const updated = [...prevInbox];
            const idx = updated.findIndex((c) => c.userInfo?._id?.toString() === chatUserId?.toString() || c.userInfo?.id?.toString() === chatUserId?.toString());
            const isChatOpen = selectedUserRef.current?.userId?.toString() === chatUserId?.toString();
            const timeAgo = formatTimeAgo(message.createdAt || new Date());

            if (idx > -1) {
                // Update existing conversation
                const currentConv = updated[idx];
                updated[idx] = {
                    ...currentConv,
                    lastMessage: message.message,
                    createdAt: message.createdAt,
                    timeAgo,
                    unreadCount: isChatOpen ? 0 : (currentConv.unreadCount || 0) + 1,
                };
            } else {
                // Add new conversation - handle missing user info gracefully
                const newConversation = {
                    userInfo: {
                        _id: userInfo?.id || userInfo?._id || `temp_${Date.now()}`,
                        firstname: userInfo?.firstname || userInfo?.username || "User",
                        lastname: userInfo?.lastname || "",
                        username: userInfo?.username || "",
                        image: userInfo?.image || null,
                    },
                    lastMessage: message.message,
                    createdAt: message.createdAt,
                    timeAgo,
                    unreadCount: isChatOpen ? 0 : 1,
                    _id: message._id || Date.now(),
                };
                
                updated.unshift(newConversation);
            }

            return [...updated];
        });
    };

    useEffect(() => {
        if (!socket) return;

        const handleReceiveMessage = (message) => {

            const selectedId = selectedUserRef.current?.userId?.toString();

            const senderId = message.sender?.id?.toString();
            const receiverId = message.receiver?.id?.toString();

            if (selectedId && (senderId === selectedId || receiverId === selectedId)) {
                setChatMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages, message];
                    updatedMessages.sort(
                        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
                    );
                    return updatedMessages;
                });
            }
        };

        const handleNewInboxMessageWrapper = (message) => {
            handleNewInboxMessage(message);
        };

        socket.on("receivesupportMessage", handleReceiveMessage);
        socket.on("newsupportinboxMessage", handleNewInboxMessageWrapper);

        return () => {
            socket.off("receivesupportMessage", handleReceiveMessage);
            socket.off("newsupportinboxMessage", handleNewInboxMessageWrapper);
        };
    }, [socket]);

    useEffect(() => {
        if (socket) fetchInbox(1, false);
    }, [socket, fetchInbox]);

    // Handle user selection
    const handleUserSelect = (conv) => {
        const userInfo = conv.userInfo;
        setSelectedUser({ userId: userInfo._id, username: userInfo.username });
        fetchChatMessages(userInfo._id);

        // Clear unread count
        setInbox((prev) =>
            prev.map((c) =>
                c.userInfo?._id === userInfo._id ? { ...c, unreadCount: 0 } : c
            )
        );
    };


    // Handle search
    const handleSearch = (value) => {
        setSearchTerm(value);
        setInboxPage(1);
        setInbox([]);
        fetchInbox(1, false);
    };


    // Handle scroll for virtual scrolling
    const handleInboxScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
        
        if (isNearBottom && hasMoreInbox) {
            const nextPage = Math.floor(inbox.length / inboxLimit) + 1;
            if (nextPage <= inboxTotalPages) {
                fetchInbox(nextPage, true);
            }
        }
    }, [hasMoreInbox, inbox.length, inboxLimit, inboxTotalPages, fetchInbox]);

    // Send message
    const handleSendMessage = () => {
        if (!messageInput.trim() || !selectedUser || !socket) return;

        const messageText = messageInput.trim();
        const newMessage = {
            receiverId: selectedUser.userId,
            receiverRole: "user",
            message: messageText,
        };

        const tempMessage = {
            _id: Date.now(),
            message: messageText,
            sender: { role: "admin", id: admin?._id },
            receiver: { id: selectedUser.userId },
            createdAt: new Date().toISOString(),
        };

        socket.emit("supportchat", newMessage);
        setChatMessages((prev) => [...prev, tempMessage]);
        handleNewInboxMessage(tempMessage);

        setMessageInput("");
        setIsTyping(false);
    };

    // Handle typing indicator
    const handleTyping = (e) => {
        setMessageInput(e.target.value);
        if (!isTyping && e.target.value.trim()) {
            setIsTyping(true);
        } else if (isTyping && !e.target.value.trim()) {
            setIsTyping(false);
        }
    };

    return (
        <Container
            maxWidth="lg"
            sx={{
                height: "90vh",
                mt: 2,
                mb: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    height: "100%",
                    borderRadius: 3,
                overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            }}
        >
            <Grid container sx={{ height: "100%" }}>
                {(!isMobile || !selectedUser) && (
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                                bgcolor: "background.paper",
                            }}
                        >
                            {/* Header */}
                            <Box 
                                sx={{ 
                                    p: 3, 
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                }}
                            >
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 600,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1
                                    }}
                                >
                                    💬 Support Inbox
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                                    {inbox.length} conversations
                                </Typography>
                            </Box>

                            {/* Search Bar */}
                            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search conversations..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: 2,
                                            bgcolor: "background.default",
                                        }
                                    }}
                                />
                        </Box>
                            {/* Conversations List */}
                            <Box 
                                ref={inboxScrollRef}
                                onScroll={handleInboxScroll}
                                sx={{ 
                                    flexGrow: 1, 
                                    overflowY: "auto",
                                    "&::-webkit-scrollbar": {
                                        display: "none",
                                    },
                                    scrollbarWidth: "none",
                                    msOverflowStyle: "none",
                                }}
                            >
                            {inbox.length === 0 ? (
                                    <Box 
                                        sx={{ 
                                            p: 4, 
                                            textAlign: "center",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 2
                                        }}
                                    >
                                        <Typography variant="h6" color="text.secondary">
                                            No conversations found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm ? "Try adjusting your search terms" : "Start a conversation with a user"}
                                        </Typography>
                                    </Box>
                            ) : (
                                <List disablePadding>
                                        {inbox.map((conv) => {
                                            const user = conv?.userInfo;
                                            const isSelected = selectedUser?.userId === user?._id;
                                        return (
                                                <Fade in key={conv._id || conv.conversationKey} timeout={300}>
                                            <ListItem
                                                button
                                                onClick={() => handleUserSelect(conv)}
                                                sx={{
                                                    bgcolor: isSelected ? "primary.light" : "transparent",
                                                            "&:hover": { 
                                                                bgcolor: isSelected ? "primary.light" : "action.hover",
                                                                transform: "translateX(4px)",
                                                            },
                                                    cursor: "pointer",
                                                    px: 2,
                                                            py: 2,
                                                            mx: 1,
                                                            my: 0.5,
                                                            borderRadius: 2,
                                                            transition: "all 0.2s ease-in-out",
                                                            border: isSelected ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                                    <Avatar
                                                        src={user.image ? `${IMAGE_BASE}${user.image}` : undefined}
                                                        alt={capitalizeFirstLetter(user.firstname)}
                                                                sx={{ 
                                                                    width: 48, 
                                                                    height: 48, 
                                                                    mr: 2,
                                                                    bgcolor: "primary.main",
                                                                    fontSize: "1.2rem",
                                                                    fontWeight: 600
                                                                }}
                                                    >
                                                        {user.firstname?.[0]?.toUpperCase() || "U"}
                                                    </Avatar>
                                                            
                                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                                                    <Typography 
                                                                        variant="subtitle2" 
                                                                        sx={{ 
                                                                            fontWeight: 600,
                                                                            color: isSelected ? "primary.contrastText" : "text.primary",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap"
                                                                        }}
                                                                    >
                                                                        {`${capitalizeFirstLetter(user.firstname || user.username || "Unknown")} ${capitalizeFirstLetter(user?.lastname || "")}`}
                                                                    </Typography>
                                                            <Typography
                                                                        variant="caption" 
                                                                        sx={{ 
                                                                            color: isSelected ? "primary.contrastText" : "text.secondary",
                                                                            fontSize: "0.7rem"
                                                                        }}
                                                                    >
                                                                        {conv.timeAgo}
                                                            </Typography>
                                                </Box>

                                                                <Typography
                                                                    variant="body2"
                                                        sx={{
                                                                        color: isSelected ? "primary.contrastText" : "text.secondary",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                        fontSize: "0.85rem",
                                                                        opacity: 0.8
                                                                    }}
                                                                >
                                                                    {conv.lastMessage || "No messages yet"}
                                                                </Typography>
                                                    </Box>

                                                            {/* Unread Badge */}
                                                            {conv.unreadCount > 0 && (
                                                                <Chip
                                                                    label={conv.unreadCount > 99 ? "99+" : conv.unreadCount}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: "error.main",
                                                                        color: "error.contrastText",
                                                                        fontWeight: 600,
                                                                        minWidth: 24,
                                                                        height: 24,
                                                                        fontSize: "0.7rem",
                                                                        "& .MuiChip-label": {
                                                                            px: 1
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                            </ListItem>
                                                </Fade>
                                        );
                                    })}
                                </List>
                                )}
                        </Box>
                    </Grid>
                )}
                {/* Right Chat Panel */}
                {(!isMobile || selectedUser) && (
                    <Grid
                        item
                        xs={12}
                        sm={8}
                        md={9}
                        sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                                bgcolor: "background.default",
                        }}
                    >
                            {/* Chat Header */}
                        <Box
                            sx={{
                                    p: 3,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    bgcolor: "background.paper",
                                display: "flex",
                                alignItems: "center",
                                    justifyContent: "space-between",
                                    minHeight: 80,
                            }}
                        >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {isMobile && selectedUser && (
                                        <IconButton
                                    onClick={() => setSelectedUser(null)}
                                    sx={{
                                                color: "text.primary",
                                                mr: 1,
                                            }}
                                        >
                                            <ArrowBackIcon />
                                        </IconButton>
                                    )}
                                    
                                    {selectedUser ? (
                                        <>
                                            <Avatar
                                                src={selectedUser.image ? `${IMAGE_BASE}${selectedUser.image}` : undefined}
                                                alt={capitalizeFirstLetter(selectedUser.firstname)}
                                                sx={{ 
                                                    width: 48, 
                                                    height: 48,
                                                    bgcolor: "primary.main",
                                        fontSize: "1.2rem",
                                                    fontWeight: 600
                                                }}
                                            >
                                                {selectedUser.firstname?.[0]?.toUpperCase() || "U"}
                                            </Avatar>
                                            
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ 
                                                        fontWeight: 600,
                                                        color: "text.primary",
                                                        lineHeight: 1.2
                                                    }}
                                                >
                                                    {`${capitalizeFirstLetter(selectedUser.firstname || selectedUser.username || "Unknown")} ${capitalizeFirstLetter(selectedUser.lastname || "")}`}
                                                </Typography>
                                            </Box>
                                        </>
                                    ) : (
                                        <Box sx={{ textAlign: "center", width: "100%" }}>
                            <Typography
                                variant="h6"
                                                sx={{ 
                                                    color: "text.secondary",
                                                    fontWeight: 500
                                                }}
                            >
                                                💬 Select a conversation to start chatting
                            </Typography>
                        </Box>
                                    )}
                                </Box>
                                
                                {selectedUser && (
                                    <IconButton sx={{ color: "text.secondary" }}>
                                        <MoreVertIcon />
                                    </IconButton>
                                )}
                            </Box>
                            {/* Messages Area */}
                        <Box
                            ref={chatBoxRef}
                            sx={{
                                flexGrow: 1,
                                p: 2,
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                    gap: 2,
                                    bgcolor: "background.default",
                                    minHeight: 0,
                                    "&::-webkit-scrollbar": {
                                        width: "6px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        background: "transparent",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        background: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                                        borderRadius: "3px",
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": {
                                        background: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                                    },
                            }}
                        >
                            {chatLoading ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                        <Box sx={{ textAlign: "center" }}>
                                    <Loader />
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                                Loading messages...
                                </Typography>
                                        </Box>
                                    </Box>
                            ) : chatMessages.length === 0 ? (
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                            flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                            textAlign: "center",
                                            gap: 2,
                                            p: 4,
                                        }}
                                    >
                                        <Typography variant="h6" color="text.secondary">
                                            💬 No messages yet
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Start the conversation by sending a message
                                        </Typography>
                                </Box>
                            ) : (
                                (() => {
                                    const groupedMessages = {};
                                    chatMessages.forEach((msg) => {
                                        const date = new Date(msg.createdAt).toLocaleDateString("en-GB");
                                        if (!groupedMessages[date]) groupedMessages[date] = [];
                                        groupedMessages[date].push(msg);
                                    });

                                    return Object.entries(groupedMessages).map(([date, messagesOnDate]) => (
                                        <React.Fragment key={date}>
                                                {/* Date Separator */}
                                            <Box sx={{ textAlign: "center", my: 2 }}>
                                                    <Chip
                                                        label={date}
                                                        size="small"
                                                    sx={{
                                                            bgcolor: theme.palette.mode === 'dark' ? "grey.700" : "background.paper",
                                                            color: "text.secondary",
                                                            fontWeight: 500,
                                                            boxShadow: theme.palette.mode === 'dark' 
                                                                ? "0 2px 8px rgba(0,0,0,0.3)"
                                                                : "0 2px 8px rgba(0,0,0,0.1)",
                                                        }}
                                                    />
                                            </Box>
                                                
                                                {/* Messages */}
                                                {messagesOnDate.map((msg, index) => {
                                                const isAdminSender = ["admin", "SuperAdmin", "Mgt", "Junior"].includes(msg.sender?.role);
                                                const isMe = isAdminSender;
                                                    const prevMsg = index > 0 ? messagesOnDate[index - 1] : null;
                                                    const nextMsg = index < messagesOnDate.length - 1 ? messagesOnDate[index + 1] : null;
                                                    
                                                    const isConsecutive = prevMsg && 
                                                        prevMsg.sender?.role === msg.sender?.role &&
                                                        new Date(msg.createdAt) - new Date(prevMsg.createdAt) < 5 * 60 * 1000; // 5 minutes
                                                    
                                                    const isLastInGroup = !nextMsg || 
                                                        nextMsg.sender?.role !== msg.sender?.role ||
                                                        new Date(nextMsg.createdAt) - new Date(msg.createdAt) > 5 * 60 * 1000;

                                                return (
                                                        <Fade in key={msg._id} timeout={300}>
                                                    <Box
                                                        sx={{
                                                                    display: "flex",
                                                                    justifyContent: isMe ? "flex-end" : "flex-start",
                                                                    mb: isLastInGroup ? 1 : 0.5,
                                                                    px: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                        maxWidth: "70%",
                                                                        minWidth: "120px",
                                                                        position: "relative",
                                                                    }}
                                                                >
                                                                    {/* Message Bubble */}
                                                                    <Paper
                                                                        elevation={2}
                                                                        sx={{
                                                                            bgcolor: isMe ? "primary.main" : theme.palette.mode === 'dark' ? "grey.800" : "background.paper",
                                                                            color: isMe ? "primary.contrastText" : "text.primary",
                                                                            borderRadius: isMe 
                                                                                ? (isConsecutive ? "18px 18px 4px 18px" : "18px 18px 4px 18px")
                                                                                : (isConsecutive ? "18px 18px 18px 4px" : "4px 18px 18px 18px"),
                                                                            px: 2,
                                                                            py: 1.5,
                                                                            wordBreak: "break-word",
                                                                            position: "relative",
                                                                            boxShadow: isMe 
                                                                                ? "0 2px 12px rgba(25, 118, 210, 0.3)"
                                                                                : theme.palette.mode === 'dark' 
                                                                                    ? "0 2px 12px rgba(0,0,0,0.3)"
                                                                                    : "0 2px 12px rgba(0,0,0,0.1)",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    whiteSpace: "pre-line",
                                                                                lineHeight: 1.4,
                                                                                fontSize: "0.9rem",
                                                                }}
                                                            >
                                                                {msg.message}
                                                            </Typography>
                                                                        
                                                                        {/* Time Stamp */}
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize: "0.7rem",
                                                                                color: isMe ? "rgba(255,255,255,0.7)" : "text.secondary",
                                                                                display: "block",
                                                                                textAlign: "right",
                                                                                mt: 0.5,
                                                                }}
                                                            >
                                                                {new Date(msg.createdAt).toLocaleTimeString([], {
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                })}
                                                            </Typography>
                                                                    </Paper>
                                                                    
                                                                    {/* Message Status Indicator */}
                                                                    {isMe && (
                                                                        <Box
                                                                            sx={{
                                                                                position: "absolute",
                                                                                right: -8,
                                                                                top: "50%",
                                                                                transform: "translateY(-50%)",
                                                                                width: 0,
                                                                                height: 0,
                                                                                borderLeft: "8px solid transparent",
                                                                                borderRight: "8px solid transparent",
                                                                                borderTop: `8px solid ${theme.palette.primary.main}`,
                                                                            }}
                                                                        />
                                                                    )}
                                                        </Box>
                                                    </Box>
                                                        </Fade>
                                                );
                                            })}
                                        </React.Fragment>
                                    ));
                                })()
                            )}
                        </Box>
                        {/* Message Input */}
                        {selectedUser && (
                            <Box
                                sx={{
                                        p: 3,
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                        bgcolor: "background.paper",
                                    display: "flex",
                                        gap: 2,
                                        alignItems: "flex-end",
                                }}
                            >
                                <TextField
                                    variant="outlined"
                                        placeholder="Type your message..."
                                    value={messageInput}
                                        onChange={handleTyping}
                                    fullWidth
                                        multiline
                                        maxRows={4}
                                    InputProps={{
                                            sx: { 
                                                borderRadius: 3,
                                                bgcolor: theme.palette.mode === 'dark' ? "grey.800" : "background.default",
                                                color: "text.primary",
                                                "&:hover": {
                                                    bgcolor: theme.palette.mode === 'dark' ? "grey.700" : "background.default",
                                                },
                                                "&.Mui-focused": {
                                                    bgcolor: theme.palette.mode === 'dark' ? "grey.700" : "background.paper",
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: theme.palette.mode === 'dark' ? "grey.600" : "grey.300",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: theme.palette.mode === 'dark' ? "grey.500" : "grey.400",
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                />
                                    <Button 
                                        variant="contained" 
                                        onClick={handleSendMessage}
                                        disabled={!messageInput.trim()}
                                        sx={{
                                            borderRadius: 3,
                                            minWidth: 60,
                                            height: 40,
                                            px: 3,
                                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                                            "&:hover": {
                                                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                                                transform: "translateY(-1px)",
                                            },
                                            transition: "all 0.2s ease-in-out",
                                        }}
                                        startIcon={<SendIcon />}
                                    >
                                    Send
                                </Button>
                            </Box>
                        )}
                    </Grid>
                )}
            </Grid>
            </Paper>
        </Container>
    );
}